import { Component, MouseEvent, ReactNode } from 'react';
import { RelayProp, createFragmentContainer, graphql } from 'react-relay';
import { connect, DispatchProp } from 'react-redux';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/handleLocaleUrl';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Button, ButtonProps } from 'dibs-elements/exports/Button';
import { currencies } from 'dibs-currency-helpers';

// mutations
import { SharedModifyPortfolioItems } from '../../mutations/FindingSharedModifyPortfolioItems';

// helpers
import get from 'lodash.get';
import serverVars from 'server-vars';
import { Maybe } from 'dibs-ts-utils/exports/Maybe';
import { trackPurchaseClick } from '../tracking/actionButtonsTracking';
import { setCartItems } from '../../actions/sharedMobileActions';

import { PdpSharedPurchaseButton_item$data } from './__generated__/PdpSharedPurchaseButton_item.graphql';

// styles
import styles from './PdpSharedButtons.scss';

type Props = {
    item: PdpSharedPurchaseButton_item$data;
    quantitySelected?: string;
    relay: RelayProp;
    itemId: string;
    userId?: Maybe<string>;
    size?: 'small' | 'medium' | 'large';
    isPrimary?: boolean;
    skuId?: string;
    disabled?: boolean;
    label?: ReactNode;
} & DispatchProp;

class PdpSharedPurchaseButtonComponentClass extends Component<Props> {
    static defaultProps = {
        size: 'large',
        isPrimary: true,
        disabled: false,
    };

    constructor(props: Props) {
        super(props);

        this.onClick = this.onClick.bind(this);
    }

    onClick(event?: MouseEvent): void {
        const { item } = this.props;
        const hasPrivateListing = get(item, 'hasPrivateListing');
        trackPurchaseClick(item, event);

        if (!hasPrivateListing) {
            this.addToCart();
        }

        this.redirectToCheckout();
    }

    addToCart(): void {
        const { dispatch, userId, itemId } = this.props;
        if (!userId) {
            // If no user, store item - it can be ADDed to cart when user logs in later
            dispatch(setCartItems(itemId));
        } else {
            // ADD item to user's cart
            this.addToCartMutation();
        }
    }

    addToCartMutation(): void {
        const { relay, userId, itemId } = this.props;
        SharedModifyPortfolioItems.commit(relay.environment, {
            action: 'ADD',
            itemId,
            portfolioType: 'CART',
            userId,
            onCompleted: () => {
                this.redirectToCheckout();
            },
            onError: () => {
                this.redirectToCheckout();
            },
        });
    }

    getCheckoutUrl(): string {
        const { quantitySelected, itemId, skuId } = this.props;
        const href = `/checkout/id-${itemId}/`;
        const params = [];
        const forcedCurrency = serverVars.get('forcedCurrency') || '';

        if (quantitySelected) {
            params.push(`quantity=${quantitySelected}`);
        }
        if (skuId) {
            params.push(`skuId=${skuId}`);
        }
        if (currencies.includes(forcedCurrency)) {
            params.push(`offerCurrency=${forcedCurrency}`);
        }
        return `${href}${params.length ? '?' : ''}${params.join('&')}`;
    }

    redirectToCheckout(): void {
        const checkoutUrl = this.getCheckoutUrl();
        window.location.assign(handleLocaleUrl(checkoutUrl, GLOBAL_CLIENT_ONLY_LOCALE));
    }

    render(): ReactNode {
        const { isPrimary, item, disabled, label } = this.props;
        const isPrivateVisibilityType = get(item, 'isPrivateVisibilityType');
        const additionalProps: Partial<ButtonProps> = {};
        if (isPrivateVisibilityType) {
            additionalProps.type = 'primaryAlt';
        } else if (!isPrimary) {
            additionalProps.type = 'secondary';
        }

        return (
            <Button
                className={`${styles.button} ${styles.fullWidth}`}
                dataTn="item-actions-purchase-item"
                onClick={this.onClick}
                size={this.props.size}
                disabled={disabled}
                {...additionalProps}
            >
                {label ? (
                    <>{label}</>
                ) : (
                    <FormattedMessage
                        id="PdpSharedPurchaseButton.purchaseCta"
                        defaultMessage="Purchase"
                    />
                )}
            </Button>
        );
    }
}

export const PdpSharedPurchaseButtonComponent = connect()(PdpSharedPurchaseButtonComponentClass);

export const PdpSharedPurchaseButton = createFragmentContainer(PdpSharedPurchaseButtonComponent, {
    item: graphql`
        fragment PdpSharedPurchaseButton_item on Item {
            isPrivateVisibilityType
            hasPrivateListing
            pdpTrackingVpvParams(userId: $userId) @include(if: $isClient)
            ...actionButtonsTracking_item @arguments(fetchAuction: $fetchAuction)
        }
    `,
});
